import posthog from 'posthog-js'

import { isProduction, isStaging, POSTHOG_HOST } from '@/config/config'
import { redactCheckoutIds, scrubRecordedRequest } from '@/lib/analyticsPrivacy'
import { rememberAttribution } from '@/lib/attribution'
import { readCrossDomainBootstrap } from '@/lib/crossDomainTracking'
import { startErrorReporting } from '@/lib/errorReporting'
import { tagProduct } from '@/lib/productTags'
import { reloadOnStaleChunk } from '@/lib/staleChunk'

/** Starts error reporting, analytics and attribution; the entry calls it before rendering. */
export function startApp(): void {
  startErrorReporting()
  reloadOnStaleChunk()

  // Read (and strip from the URL) any identifiers the other split-origin host handed
  // over. Runs in every env, not just production, so the parameters never linger in
  // the address bar of a preview — but it has to happen before `posthog.init`, which
  // only accepts them via `bootstrap`.
  const crossDomainBootstrap = readCrossDomainBootstrap()

  // The server puts this environment's token in the page (cailabs/insure/views/base_view.py), so
  // staging reports to its own project; a page served without one leaves the SDK off.
  const posthogToken = window.__CAI_CONFIG__?.posthogToken ?? ''

  if ((isProduction || isStaging) && posthogToken) {
    posthog.init(posthogToken, {
      api_host: POSTHOG_HOST,
      before_send: (event) => tagProduct(redactCheckoutIds(event)),
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      capture_pageleave: 'if_capture_pageview',
      defaults: '2026-01-30',
      bootstrap: crossDomainBootstrap,
      // Input masking keeps typed sign-in emails and one-time codes out of recordings. It only
      // covers input values: rendered text needs a ph-no-capture class of its own.
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: { password: true },
        maskCapturedNetworkRequestFn: scrubRecordedRequest,
      },
    })
  }

  rememberAttribution()
}
